import { Card, Typography, Form, Input, InputNumber, Select, Button, Row, Col, Space, Tag, AutoComplete, DatePicker, Upload, Divider, Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, InfoCircleOutlined, InboxOutlined, FileTextOutlined,
    PlusOutlined, AppstoreAddOutlined, ScanOutlined, DeleteOutlined, SwapOutlined,
} from "@ant-design/icons"

import { StockTransferService } from '../../../Services/Manage/StockTransferService'
import { ItemService } from '../../../Services/Manage/ItemService'
import { GoodsReceiptService } from '../../../Services/Manage/GoodsReceiptService'
import { StockLedgerService } from '../../../Services/Manage/StockLedgerService'
import { UserService } from '../../../Services/Manage/UserService'
import { ENDPOINT } from '../../../Services/ENDPOINT'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
  .st-equal-height .ant-upload-drag { height: 132px; }
  .st-equal-height textarea.ant-input { height: 132px !important; resize: none; }
`;

const CardTitle = ({ icon, no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const priorityOptions = [
    { value: "Low", label: "Low" },
    { value: "Normal", label: "Normal" },
    { value: "High", label: "High" },
    { value: "Urgent", label: "Urgent" },
];

const statusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "In Transit", label: "In Transit" },
    { value: "Completed", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
];

const AddStockTransfer = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [transferNo, setTransferNo] = useState("ST-2024-000001");
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [transferTypeOptions, setTransferTypeOptions] = useState([]);
    const [reasonOptions, setReasonOptions] = useState([]);

    const [rows, setRows] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [itemSearch, setItemSearch] = useState("");

    const [scanModalOpen, setScanModalOpen] = useState(false);
    const [scanValue, setScanValue] = useState("");

    const [userMap, setUserMap] = useState({});
    const [receivedQtyMap, setReceivedQtyMap] = useState({});

    const [editST, setEditST] = useState(null);
    const [saving, setSaving] = useState(false);
    const [documentFiles, setDocumentFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;
    const id = location.state?.id;
    const action = location.state?.action;
    const enableEdit = location.state?.enableEdit;
    const from = location.state?.from || '/inventory/stock-transfer';
    const isReadOnly = !enableEdit && !!editST;
    const firstOf = (v) => (Array.isArray(v) ? v[0] : v);

    const getUserMap = async () => {
        try {
            const request = await UserService.getAllUsers();
            const map = {};
            (request.data || []).forEach((u) => {
                if (u.user_id) map[String(u.user_id)] = u.name;
            });
            setUserMap(map);
        } catch (error) { console.error(error); }
    };

    const getConfigOptions = async () => {
        try {
            const fields = ["source_warehouse", "transfer_type", "reason"];
            const results = await Promise.all(fields.map((f) => StockTransferService.getDistinctValues(f)));
            const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));
            setWarehouseOptions(toOptions(results[0].data));
            setTransferTypeOptions(toOptions(results[1].data));
            setReasonOptions(toOptions(results[2].data));
        } catch (error) { console.error(error); }
    };

    const getAllItems = async () => {
        try {
            const request = await ItemService.getAllItem();
            const active = (request.data || []).filter((i) => i.is_active);
            setAllItems(active);
        } catch (error) { console.error(error); }
    };

    const getReceivedQtyMap = async () => {
        try {
            const request = await StockLedgerService.getOnHandMap();
            setReceivedQtyMap(request.data || {});
        } catch (error) { console.error(error); }
    };

    const getLastST = async () => {
        try {
            const request = await StockTransferService.getLastST();
            let nextNo = "000001";
            if (request?.status && request?.data?.transfer_no) {
                const seqPart = request.data.transfer_no.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) nextNo = String(parsed + 1).padStart(6, "0");
            }
            setTransferNo(`ST-${dayjs().format("YYYY")}-${nextNo}`);
        } catch (error) { console.error(error); }
    };

    const getSTById = async (id) => {
        try {
            const request = await StockTransferService.getSTById(id);
            const initialValues = request.data;
            setEditST(initialValues);
            setTransferNo(initialValues.transfer_no);
            setRows((initialValues.items || []).map((it, idx) => ({ ...it, key: idx })));

            let parsedAttachments = [];
            try {
                parsedAttachments = initialValues.attachments
                    ? (typeof initialValues.attachments === "string" ? JSON.parse(initialValues.attachments) : initialValues.attachments)
                    : [];
            } catch { parsedAttachments = []; }

            setExistingAttachments(
                (parsedAttachments || []).map((f, idx) => ({
                    uid: f.uid || `existing-${idx}`,
                    name: f.name || f.filename || `file-${idx}`,
                    ...f,
                    status: "done",
                    url: `${ENDPOINT.base}/${f.path || f.url}`,
                }))
            );

            form.setFieldsValue({
                ...initialValues,
                source_warehouse: initialValues.source_warehouse ? [initialValues.source_warehouse] : [],
                destination_warehouse: initialValues.destination_warehouse ? [initialValues.destination_warehouse] : [],
                transfer_type: initialValues.transfer_type ? [initialValues.transfer_type] : [],
                reason: initialValues.reason ? [initialValues.reason] : [],
                transfer_date: initialValues.transfer_date ? dayjs(initialValues.transfer_date) : null,
                required_date: initialValues.required_date ? dayjs(initialValues.required_date) : null,
            });
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getConfigOptions();
        getAllItems();
        getUserMap();
        getReceivedQtyMap();
        if (id) {
            getSTById(id);
        } else {
            getLastST();
            form.setFieldsValue({ priority: "Normal", status: "Pending", prepared_by: currentUser?.name || "" });
        }
    }, [id]);

    useEffect(() => {
        if (!Object.keys(receivedQtyMap).length) return;
        setRows((prev) => prev.map((row) => (
            row.item_id ? { ...row, available_qty: receivedQtyMap[String(row.item_id)] || 0 } : row
        )));
    }, [receivedQtyMap]);

    const uploadOneFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${ENDPOINT.StockTransfer}/upload-file`, { method: "POST", body: formData });
        const result = await res.json();
        return result?.status && result?.success ? result.file : null;
    };

    const handleRowChange = (key, field, value) => {
        setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
    };
    const handleAddRow = () => {
        setRows((prev) => [...prev, {
            key: Date.now(), item_id: null, item_name: "", description: "", hsn_sku: "",
            unit: "", available_qty: 0, transfer_qty: 0, unit_cost: 0, comments: "",
        }]);
    };

    const handleDeleteRow = (key) => {
        setRows((prev) => prev.filter((r) => r.key !== key));
    };

    const handleSelectItemForRow = (key, itemId) => {
        const selected = receivedItems.find((x) => Number(x.id) === Number(itemId));
        if (!selected) return;
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            return {
                ...row,
                item_id: selected.id,
                item_name: selected.item_name,
                description: selected.description || "",
                hsn_sku: selected.hsn_sac_code || selected.sku_barcode || "",
                unit: selected.unit || "",
                unit_cost: Number(selected.cost_price || 0),
                available_qty: receivedQtyMap[String(selected.id)] || 0,
            };
        }));
        setItemSearch("");
    };

    const handleClearItemRow = (key) => {
    setRows((prev) => prev.map((row) => {
        if (row.key !== key) return row;
        return {
            ...row,
            item_id: null,
            item_name: "",
            description: "",
            hsn_sku: "",
            unit: "",
            unit_cost: 0,
            available_qty: 0,
        };
    }));
    setItemSearch("");
};

    // ADD (new function)
    const handleBarcodeScanned = (rawValue) => {
        const code = (rawValue || "").trim();
        if (!code) {
            messageApi.warning("Please enter a barcode number");
            return;
        }

        const matched = receivedItems.find((i) => String(i.barcode || i.sku_barcode).trim() === code);

        if (!matched) {
            messageApi.warning(`No item with stock found for barcode: ${code}`);
            return;
        }

        const emptyRow = rows.find((r) => !r.item_id);
        if (emptyRow) {
            handleSelectItemForRow(emptyRow.key, matched.id);
        } else {
            const newKey = Date.now();
            setRows((prev) => [...prev, {
                key: newKey,
                item_id: matched.id,
                item_name: matched.item_name,
                description: matched.description || "",
                hsn_sku: matched.hsn_sac_code || matched.sku_barcode || "",
                unit: matched.unit || "",
                available_qty: receivedQtyMap[String(matched.id)] || 0,
                transfer_qty: 0,
                unit_cost: Number(matched.cost_price || 0),
                comments: "",
            }]);
        }

        messageApi.success(`Added: ${matched.item_name}`);
        setScanValue("");
    };

    const receivedItems = allItems.filter((i) => (receivedQtyMap[String(i.id)] || 0) > 0);

    const itemAutoOptions = receivedItems
        .filter((i) => !itemSearch || i.item_name.toLowerCase().includes(itemSearch.toLowerCase()))
        .map((i) => ({ value: String(i.id), label: `${i.item_name}${i.sku_barcode ? ` (${i.sku_barcode})` : ""}` }));

    const totalItems = rows.length;
    const totalQty = rows.reduce((sum, r) => sum + Number(r.transfer_qty || 0), 0);
    const totalValue = rows.reduce((sum, r) => sum + Number(r.transfer_qty || 0) * Number(r.unit_cost || 0), 0);

    const onFinish = async (values) => {
        await performSave(values);
    };

    const handleSaveDraft = async () => {
        const values = form.getFieldsValue();
        await performSave(values);
    };

    const performSave = async (values) => {
        if (!rows.length) {
            messageApi.warning("Please add at least one item to transfer");
            return;
        }
        setSaving(true);
        try {
            let uploadedAttachments = [...existingAttachments];
            for (const f of documentFiles) {
                const uploaded = await uploadOneFile(f);
                if (uploaded) uploadedAttachments.push(uploaded);
            }

            const payload = {
                attachments: JSON.stringify(uploadedAttachments),
                ...values,
                source_warehouse: firstOf(values.source_warehouse),
                destination_warehouse: firstOf(values.destination_warehouse),
                transfer_type: firstOf(values.transfer_type),
                reason: firstOf(values.reason),

                transfer_no: transferNo,
                transfer_date: values.transfer_date ? dayjs(values.transfer_date).format("YYYY-MM-DD") : null,
                required_date: values.required_date ? dayjs(values.required_date).format("YYYY-MM-DD") : null,
                status: values.status || editST?.status || "Pending",
                added_by: editST ? editST.added_by : currentUserId,
                items: rows.map(({ key, ...rest }) => rest),
            };

            let request;
            if (editST) {
                request = await StockTransferService.updateST(payload, id);
            } else {
                request = await StockTransferService.createST(payload);
            }

            if (request?.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error in onFinish:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSaving(false);
        }
    };


    const handleDelete = async () => {
        try {
            const request = await StockTransferService.deleteST(id);
            if (request.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request.message);
            }
        } catch (error) { console.error(error); }
    };

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined style={{ cursor: "pointer", fontSize: "20px" }} onClick={() => navigate(from)} />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {editST ? `${action} Stock Transfer` : 'New Stock Transfer'}{' '}
                                    {editST && (
                                        <Tag color="cyan" style={{ fontSize: '16px', padding: '6px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            {editST.transfer_no}
                                        </Tag>
                                    )}
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    action === 'View' ? null : !enableEdit && editST ? (
                        <Button size="large" danger onClick={() => navigate("/inventory/stock-transfer/edit-stock-transfer", { state: { id, action: 'Edit', enableEdit: true, from } })}>
                            Enable Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button size="large" loading={saving} onClick={handleSaveDraft}>Save as Draft</Button>
                            <Button
                                type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                            >
                                {editST ? "Update Stock Transfer" : "Save Stock Transfer"}
                            </Button>
                        </Space>
                    )
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form layout="vertical" form={form} onFinish={onFinish} size="middle" disabled={isReadOnly}>
                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={1} text="Transfer Details" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Transfer No.">
                                            <Input size="large" readOnly value={transferNo} placeholder="Auto generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="transfer_date" label="Transfer Date">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="required_date" label="Required Date (Optional)">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reference_no" label="Reference No. (Optional)">
                                            <Input size="large" placeholder="Enter reference number" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="transfer_type" label="Transfer Type" rules={[{ required: true, message: 'Please select transfer type' }]}>
                                            <Select options={transferTypeOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type transfer type" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Please select reason' }]}>
                                            <Select options={reasonOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type reason" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="priority" label="Priority">
                                            <Select options={priorityOptions} size="large" placeholder="Select priority" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="status" label="Status">
                                            <Select options={statusOptions} size="large" placeholder="Select status" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="remarks" label="Remarks">
                                            <TextArea rows={3} maxLength={250} showCount placeholder="Enter remarks (optional)" style={{ resize: 'none' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<SwapOutlined />} no={2} text="Source & Destination" />}>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Typography.Text strong style={{ color: '#1658b4' }}>From (Source Warehouse)</Typography.Text>
                                        <Row gutter={16} style={{ marginTop: 8 }}>
                                            <Col xs={24} md={12}>
                                                <Form.Item name="source_warehouse" label="Warehouse" rules={[{ required: true, message: 'Please select source warehouse' }]}>
                                                    <Select options={warehouseOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type warehouse" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item name="source_address" label="Address">
                                                    <TextArea rows={2} placeholder="Enter source warehouse address" style={{ resize: 'none' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Typography.Text strong style={{ color: '#1658b4' }}>To (Destination Warehouse)</Typography.Text>
                                        <Row gutter={16} style={{ marginTop: 8 }}>
                                            <Col xs={24} md={12}>
                                                <Form.Item name="destination_warehouse" label="Warehouse" rules={[{ required: true, message: 'Please select destination warehouse' }]}>
                                                    <Select options={warehouseOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type warehouse" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item name="destination_address" label="Address">
                                                    <TextArea rows={2} placeholder="Enter destination warehouse address" style={{ resize: 'none' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Card>
                            <Card
                                title={<CardTitle icon={<InfoCircleOutlined />} no={3} text="Items" />}
                                extra={
                                    <Space>
                                        <Button icon={<PlusOutlined />} type="primary" onClick={handleAddRow}
                                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                                            Add Item
                                        </Button>
                                        {/* <Button icon={<AppstoreAddOutlined />}>Add Multiple Items</Button> */}
                                        <Button icon={<ScanOutlined />} onClick={() => setScanModalOpen(true)}>Scan Barcode</Button>                                    </Space>
                                }
                            >
                                {rows.length > 0 && (
                                    <Row gutter={8} style={{ padding: "0 8px", marginBottom: 6 }}>
                                        <Col xs={0} md={1}><Text type="secondary" strong style={{ fontSize: 12 }}>#</Text></Col>
                                        <Col xs={24} md={5}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>Item / Product</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Description</Text>
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>HSN/SKU</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Unit</Text>
                                        </Col>
                                        <Col xs={6} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>Available Qty (Source)</Text></Col>
                                        <Col xs={6} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>Transfer Qty</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Unit</Text></Col>
                                        <Col xs={12} md={5}><Text type="secondary" strong style={{ fontSize: 12 }}>Comments (Optional)</Text></Col>
                                        <Col xs={6} md={2}></Col>
                                    </Row>
                                )}

                                {rows.map((row, idx) => (
                                    <Row key={row.key} gutter={8} align="middle" style={{ marginBottom: 8, border: "1px solid #f0f0f0", borderRadius: 8, padding: 8 }}>
                                        <Col xs={0} md={1}><Text type="secondary">{idx + 1}</Text></Col>
                                        <Col xs={24} md={5}>
                                            {row.item_id ? (
                                                <>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{row.item_name}</div>
                                                    <div style={{ fontSize: 12, color: '#888' }}>{row.description}</div>
                                                </>
                                            ) : (
                                                // <AutoComplete
                                                //     style={{ width: "100%" }}
                                                //     options={itemAutoOptions}
                                                //     value={row.item_name}
                                                //     placeholder="Type item / product"
                                                //     filterOption={false}
                                                //     allowClear
                                                //     onSearch={(val) => setItemSearch(val)}
                                                //     onSelect={(val) => handleSelectItemForRow(row.key, val)}
                                                //     onChange={(val) => handleRowChange(row.key, "item_name", val)}
                                                // />

                                                <AutoComplete
    style={{ width: "100%" }}
    options={itemAutoOptions}
    value={row.item_name}
    placeholder="Search item / product"
    filterOption={false}
    allowClear
    notFoundContent="No matched items / products"
    onSearch={(val) => {
        setItemSearch(val);
        handleRowChange(row.key, "item_name", val);
    }}
    onSelect={(val) => handleSelectItemForRow(row.key, val)}
    onClear={() => handleClearItemRow(row.key)}
    onBlur={() => {
        if (!row.item_id) {
            handleClearItemRow(row.key);
        }
    }}
/>
                                            )}
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <div style={{ fontSize: 13 }}>{row.hsn_sku}</div>
                                            <div style={{ fontSize: 12, color: '#888' }}>{row.unit}</div>
                                        </Col>
                                      <Col xs={6} md={3}>
                                            <InputNumber
                                                min={0}
                                                style={{ width: '100%' }}
                                                value={row.available_qty}
                                                readOnly
                                            />
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <InputNumber min={0} max={Number(row.available_qty || 0) || undefined} style={{ width: '100%' }} value={row.transfer_qty}
                                                onChange={(v) => handleRowChange(row.key, "transfer_qty", v)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Input size="middle" value={row.unit} onChange={(e) => handleRowChange(row.key, "unit", e.target.value)} />
                                        </Col>
                                        <Col xs={12} md={5}>
                                            <Input size="middle" placeholder="For branch demand" value={row.comments}
                                                onChange={(e) => handleRowChange(row.key, "comments", e.target.value)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleDeleteRow(row.key)} />
                                        </Col>
                                    </Row>
                                ))}
                                {rows.length === 0 && (
                                    <Text type="secondary">Add items to transfer between warehouses</Text>
                                )}
                                {rows.length > 0 && (
                                    <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Total Items: {rows.length}</div>
                                )}
                            </Card>

                            <Card title={<CardTitle icon={<FileTextOutlined />} no={4} text="Additional Information" />} className="st-equal-height">
                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <Form.Item label="Attachments (Optional)">
                                            <Upload.Dragger
                                                multiple
                                                beforeUpload={(file) => { setDocumentFiles((prev) => [...prev, file]); return false; }}
                                                onRemove={(file) => {
                                                    if (file.status === "done") {
                                                        setExistingAttachments((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    } else {
                                                        setDocumentFiles((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    }
                                                }}
                                                fileList={[...existingAttachments, ...documentFiles]}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click or drag files to upload</p>
                                                <p className="ant-upload-hint">PNG, JPG, PDF (Max. 5MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="prepared_by" label="Prepared By">
                                            <Input size="large" placeholder="Enter preparer name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="notes" label="Notes (Optional)">
                                            <TextArea maxLength={250} showCount placeholder="Enter notes (optional)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>

                        {!isReadOnly && (
                            <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
                                <Space>
                                    <Button size='large' onClick={() => navigate(from)}>Cancel</Button>
                                    {editST && (
                                        <Button size="large" danger onClick={handleDelete}>Delete</Button>
                                    )}
                                    <Button
                                        type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        {editST ? "Update Stock Transfer" : "Save Stock Transfer"}
                                    </Button>
                                </Space>
                            </div>
                        )}

                        {editST && (
                            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#999' }}>
                                Created by: {userMap[String(editST.added_by)] || editST.added_by || '-'}
                                {'  |  '}
                                Last updated: {editST.updated_at ? dayjs(editST.updated_at).format('DD-MM-YYYY HH:mm') : '-'}
                            </div>
                        )}
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Transfer Summary</span>} size="small">
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Total Items", totalItems],
                                    ["Total Quantity", totalQty.toFixed(2)],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                                <Divider style={{ margin: "6px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Total Value (Source Cost)</Text>
                                    <Text strong>₹ {totalValue.toLocaleString('en-IN')}</Text>
                                </div>
                            </Space>
                        </Card>

                        <Card style={{ marginTop: 16 }} size="small" title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Item Summary</span>}>
                            {rows.length === 0 ? (
                                <Text type="secondary" style={{ fontSize: 12 }}>No items added yet</Text>
                            ) : (
                                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                    {rows.map((r, i) => (
                                        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                            <Text type="secondary">{r.item_name || `Item ${i + 1}`}</Text>
                                            <Text strong>{Number(r.transfer_qty || 0)}</Text>
                                        </div>
                                    ))}
                                </Space>
                            )}
                        </Card>

                        {/* <Card style={{ marginTop: 16, background: '#f6ffed', border: '1px solid #b7eb8f' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong style={{ color: '#389e0d' }}>Important Notes</Typography.Text>
                            <div style={{ marginTop: 10, fontSize: 12, color: '#389e0d' }}>
                                <div style={{ marginBottom: 6 }}>✓ Stock will be deducted from source warehouse and added to destination warehouse after approval.</div>
                                <div style={{ marginBottom: 6 }}>✓ Ensure items and quantities are correct before saving.</div>
                                <div>✓ You can track this transfer in the Stock Transfer list.</div>
                            </div>
                        </Card> */}
                    </Col>
                </Row>


                <Modal
                    title="Enter Barcode"
                    open={scanModalOpen}
                    onCancel={() => { setScanModalOpen(false); setScanValue(""); }}
                    footer={null}
                    destroyOnClose
                >
                    <Space.Compact style={{ width: "100%" }}>
                        <Input
                            autoFocus
                            size="large"
                            placeholder="Enter barcode number..."
                            value={scanValue}
                            onChange={(e) => setScanValue(e.target.value)}
                            onPressEnter={() => handleBarcodeScanned(scanValue)}
                        />
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => handleBarcodeScanned(scanValue)}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            Enter
                        </Button>
                    </Space.Compact>
                </Modal>
            </Card>
        </>
    );
};

export default AddStockTransfer;